import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import View from '../../../../components/view/View';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useHistory, useParams } from 'react-router-dom';
import AssignmentMetaData from './metadata/AssignmentMetaData';
import AssignmentTips from './tips/AssignmentTips';
import AssignmentOptions from './options/AssignmentOptions';
import {
  AssignmentEditState,
  AssignmentEditStatus
} from './state/assignment-edit.types';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import AssignmentEditorContainer from './editor/AssignmentEditorContainer';
import { teacherRoutes } from '../../../../constants/routes';
import { Button } from '../../common/button/Button';
import { Edit } from '@material-ui/icons';
import colors from '../../../../styles/colors';
import { show } from 'redux-modal/lib/actions';
import {
  MODAL_ASSIGN_USER_GENERATED_CONTENT,
  MODAL_DEFAULT,
  MODAL_QUESTION_BANK,
  MODAL_USER_GENERATED_ASSIGNMENT_DELETE_PROMPT,
  MODAL_USER_GENERATED_ASSIGNMENT_EDIT
} from '../../../../constants/modals';
import { userNavigateWithClassCode } from '../../../../utils/user-route-getter';
import { grades } from '../../../../constants/global-constants';
import LoaderBackDrop from '../../common/spinner/LoaderBackDrop';
import { AssignmentActionsAsync } from './state/assignment/assignment-actions.async';
import { AssignmentActions } from './state/assignment/assignment.actions';
import { AssignmentQuestionActions } from './state/question/assignment-question.actions';
import useQueryParams from '../../../../hooks/UseQueryParams';
import { Theme, Tooltip } from '@material-ui/core';
import AutoSaveLoader from './auto-save/AutoSaveLoader';
import {
  isAssignmentAssignable,
  isAssignmentValid
} from '../../util/AssignmentValidation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '80vh'
  },
  backButton: {
    padding: '0 25px'
  },
  scrollStatic: {
    position: 'relative'
  },
  scrollFix: {
    position: 'fixed',
    right: '30px',
    [theme.breakpoints.down('md')]: {
      position: 'static'
    }
  },
  autoSave: {
    position: 'absolute',
    top: '-25px',
    right: '-5px',
    marginBottom: '10px',
    textAlign: 'right'
  }
}));

const AssignmentEditView: React.FC<any> = (props: any) => {
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { id } = params;
  const query = useQueryParams();
  const isPremade = query.isPremade === 'true';
  const classes = useStyles();
  const folderId: number = query.folderId || 0;
  const dispatch = useAppDispatch();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const assignment = useAppSelector<AssignmentEditState>(
    state => state.editAssignment
  );
  const { skills, groups, genres } = assignment;
  const userData = useAppSelector(state => state.userData);
  const dashboardUserId =
    userData.features?.userGeneratedContent?.dashboardUserId;
  useEffect(() => {
    if (!isEdit) {
      handleAssignmentModal();
    }
    if (id) {
      setLoading(true);

      dispatch(
        AssignmentActionsAsync.fetchAssignmentById(
          (id as unknown) as number,
          isPremade
        )
      )
        // @ts-ignore
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
    if (
      !assignment.skills ||
      assignment.skills.length === 0 ||
      assignment.genres.length === 0 ||
      assignment.questionTypes.length === 0
    ) {
      dispatch(AssignmentActionsAsync.fetchEditorData());
    }
    return () => {
      dispatch(AssignmentActions.reset());
    };
  }, []);

  useEffect(() => {
    if (assignment.id && assignment.id !== id) {
      history.replace(
        teacherRoutes.userGeneratedAssignmentsEdit.replace(
          ':id',
          assignment.id as string
        ) + `?isPremade=${isPremade}`
      );
    }
  }, [assignment.id]);

  const handleBackButton = () => {
    history.goBack();
  };

  const handleAssignmentEdit = (data: Partial<AssignmentEditState>) => {
    dispatch(AssignmentActions.updateAssignment(data));
  };

  const handleAssignmentCreation = async (name: string, grade: number) => {
    if (id) {
      await dispatch(
        AssignmentActionsAsync.saveAssignment(
          { ...assignment, grade, name },
          assignment.status,
          isPremade
        )
      );
    } else {
      await dispatch(AssignmentActionsAsync.create({ name, grade, folderId }));
    }
  };

  const [isEditMode, setIsEditMode] = useState(
    assignment.status !== AssignmentEditStatus.Published && !isPremade
  );
  let title = 'Assignment Editor';
  if (
    (assignment.status === AssignmentEditStatus.Published && !isEditMode) ||
    isPremade
  ) {
    title = 'Assignment Viewer';
  }

  useEffect(() => {
    setIsEditMode(
      assignment.status !== AssignmentEditStatus.Published && !isPremade
    );
  }, [assignment.status]);

  const userClasses = useAppSelector(state => state.class.classes);
  /**
   * Filters assignment and removes the ones marked for deletion
   */
  const filteredAssignment = { ...assignment };
  filteredAssignment.data = assignment.data.filter(
    assignmentQuestion => !assignmentQuestion.isDeleted
  );

  const filteredUserClasses = userClasses.filter(
    (currentClass: any) =>
      currentClass.roster.length > 0 && !currentClass.archived
  );

  const handleAssign = () => {
    dispatch(
      show('modal', {
        type: MODAL_ASSIGN_USER_GENERATED_CONTENT,
        assignment,
        userClasses: filteredUserClasses,
        dashboardUserId,
        assignmentId: assignment.id,
        isFromEdit: true
      })
    );
  };

  const handleDuplicate = async (name: string, grade: number) => {
    dispatch(
      AssignmentActionsAsync.duplicateAssignment(
        (assignment.id as unknown) as number,
        name,
        grade
      )
    );
  };

  const handleDeleteAssignment = async (shouldDelete: boolean) => {
    if (shouldDelete) {
      await dispatch(
        AssignmentActionsAsync.deleteAssignment(
          (assignment.id as unknown) as number,
          isPremade
        )
      );
    }
    history.goBack();
  };

  const handleDeleteAssignmentModal = () => {
    dispatch(
      show('modal', {
        type: MODAL_USER_GENERATED_ASSIGNMENT_DELETE_PROMPT,
        handleDeleteAssignment
      })
    );
  };
  const handleAssignmentDuplicateModal = () => {
    dispatch(
      show('modal', {
        title: 'Duplicate Assignment',
        type: MODAL_USER_GENERATED_ASSIGNMENT_EDIT,
        assignment: assignment,
        handleSubmit: handleDuplicate,
        duplicate: true,
        isEdit: isEdit,
        handleHide: (isAssignmentCreated: boolean) => {
          if (!isAssignmentCreated && !isEdit) {
            history.goBack();
          }
        }
      })
    );
  };

  const handleAssignmentModal = () => {
    dispatch(
      show('modal', {
        type: MODAL_USER_GENERATED_ASSIGNMENT_EDIT,
        handleSubmit: handleAssignmentCreation,
        isEdit: isEdit,
        assignment: assignment,
        handleClose: (isAssignmentCreated: boolean) => {
          if (!isAssignmentCreated && !isEdit) {
            history.goBack();
          }
        }
      })
    );
  };

  function handleSavePreMadeQuestions(questions: any) {
    dispatch(AssignmentQuestionActions.addPreMadeQuestions(questions));
  }

  const handleQuestionBank = () => {
    dispatch(
      show('modal', {
        type: MODAL_QUESTION_BANK,
        assignmentId: assignment.id,
        skills,
        groups,
        grades,
        genres,
        alreadyAddedQuestions: assignment.data.filter(
          question => question.isFromQuestionBank
        ),
        handleSave: (questions: any) => handleSavePreMadeQuestions(questions)
      })
    );
  };

  const handleEditMode = () => {
    dispatch(
      show('modal', {
        type: MODAL_DEFAULT,
        title: 'Editing Assignment',
        children:
          'You are about to edit an assignment that has already been assigned to users, this can affect assignment reports.',
        alignActions: 'flex-end',
        closeButton: (handleHide: any) => (
          <Button
            onClick={() => {
              handleHide();
            }}
            style={{ padding: '.5rem 1rem' }}
            variant={'outline'}
          >
            Cancel
          </Button>
        ),
        actions: (handleHide: any) => (
          <Button
            onClick={() => {
              setIsEditMode(true);
              handleHide();
            }}
            style={{ padding: '.5rem 1rem' }}
            textColor={'white'}
            color={colors.assignment.blue}
            variant={'filled'}
          >
            Ok
          </Button>
        )
      })
    );
  };

  /**
   * Auto save feature.
   *
   * First we save a ref of the current assignment data, when a user edits the assignment the isEdited prop gets updated.
   * Which causes the useEffect() hook to get triggered. This hook will check a deep comparison of changes and if the assignment
   * has been edited. If so will trigger a callback function to save the assignment.
   */
  let saveRef = useRef(assignment);
  saveRef.current = assignment;
  const isEdited = assignment.autoSave?.isEdited;
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isEdited && isEditMode) {
        dispatch(
          AssignmentActionsAsync.saveAssignment(
            { ...saveRef.current },
            assignment.status,
            isPremade,
            true
          )
        );
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [assignment]);

  const sidebarShouldScroll = isEditMode && !isPremade;

  const navigateToReportPage = () => {
    const route = userNavigateWithClassCode(teacherRoutes.assignmentReportView);
    history.push(
      `${route}?isUserGenerated=${!isPremade}&assignmentId=${assignment.id}`
    );
  };

  const handleSave = async () => {
    dispatch(
      AssignmentActionsAsync.saveAssignment(assignment, assignment.status)
    );
  };
  const isDataNotSynced = filteredAssignment.data.find(
    question => !question.isSynced
  );
  const isErrorInAssignment = !isAssignmentValid(filteredAssignment.data);
  const isAssignable = isAssignmentAssignable(assignment.data);
  const isError = !isAssignable || isErrorInAssignment || isDataNotSynced;
  return (
    <Grid container>
      {!loading ? (
        <>
          <Grid item className={classes.backButton}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="back button"
              onClick={handleBackButton}
            >
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <View title={title}>
            {isPremade && (
              <Grid container style={{ marginBottom: '1em' }}>
                <Grid item xs={12} md={9} style={{ textAlign: 'right' }}>
                  <Button
                    variant={'filled'}
                    style={{ padding: '.5rem 1rem' }}
                    textColor={'white'}
                    onClick={navigateToReportPage}
                    color={colors.assignment.turquoise}
                  >
                    View Report
                  </Button>
                </Grid>
              </Grid>
            )}
            {!isEditMode && !isPremade && (
              <Grid container style={{ marginBottom: '1em' }}>
                <Grid item xs={12} md={9} style={{ textAlign: 'right' }}>
                  <Button
                    style={{ marginRight: '4px' }}
                    variant={'outline'}
                    textColor={colors.assignment.blue}
                    color={colors.assignment.blue}
                    onClick={handleEditMode}
                    icon={Edit}
                  >
                    Edit
                  </Button>
                </Grid>
              </Grid>
            )}

            <Grid className={classes.root} container spacing={2}>
              <Grid
                item
                xs={12}
                md={!isEditMode ? 8 : 8}
                lg={!isEditMode ? 9 : 8}
              >
                <AssignmentEditorContainer
                  dashboardUserId={dashboardUserId}
                  handleQuestionBank={handleQuestionBank}
                  userClasses={filteredUserClasses}
                  originalAssignment={assignment}
                  assignment={filteredAssignment}
                  isEditMode={isEditMode}
                  showActions={isEdit}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={!isEditMode ? 4 : 4}
                lg={3}
                className={
                  sidebarShouldScroll ? classes.scrollFix : classes.scrollStatic
                }
              >
                <Grid container spacing={3} style={{ position: 'relative' }}>
                  {isEditMode && !isPremade && (
                    <Grid item xs={12} className={classes.autoSave}>
                      <AutoSaveLoader
                        isDataEdited={assignment.autoSave?.isEdited || false}
                        isSaving={assignment.autoSave?.saving}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <AssignmentMetaData
                      handleDeleteAssignment={handleDeleteAssignmentModal}
                      handleDuplicate={handleAssignmentDuplicateModal}
                      assignment={filteredAssignment}
                      isPremade={isPremade}
                      isError={
                        !isAssignable || isErrorInAssignment || isDataNotSynced
                      }
                      handleSave={handleSave}
                      handleAssign={handleAssign}
                      isEditMode={isEditMode}
                      handleAssignmentEdit={handleAssignmentEdit}
                      handleAssignmentModal={handleAssignmentModal}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 0
                    }}
                  >
                    {!isPremade && !isEditMode && (
                      <Tooltip
                        style={{
                          fontSize: '1.2rem'
                        }}
                        title={
                          isError
                            ? 'All errors must be fixed before it can be assigned.'
                            : ''
                        }
                      >
                        <div>
                          <Button
                            variant={'filled'}
                            disabled={isError}
                            onClick={handleAssign}
                            style={{ padding: '.5rem 1rem' }}
                            textColor={'white'}
                            color={colors.assignment.blue}
                          >
                            Assign
                          </Button>
                        </div>
                      </Tooltip>
                    )}
                    &nbsp;
                    {!isPremade && !isEditMode && (
                      <Button
                        variant={'filled'}
                        style={{ padding: '.5rem 1rem' }}
                        textColor={'white'}
                        onClick={navigateToReportPage}
                        color={colors.assignment.turquoise}
                      >
                        View Report
                      </Button>
                    )}
                  </Grid>
                  {!isPremade && (
                    <Grid item xs={12}>
                      <AssignmentOptions
                        assignment={assignment}
                        isEditMode={isEditMode}
                        handleAssignmentEdit={handleAssignmentEdit}
                      />
                    </Grid>
                  )}
                  {isEditMode && (
                    <Grid item xs={12}>
                      <AssignmentTips />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </View>
        </>
      ) : (
        <LoaderBackDrop loading={loading} loaderMessage={'loading'} />
      )}
    </Grid>
  );
};

export default AssignmentEditView;
